// MIGRATION: Switching from localStorage API key auth to GitHub OAuth with session cookies
// This change is intentional and required for the OAuth flow implementation.
// Authentication is now handled via secure HTTP-only cookies set by the backend.

// Get API base URL based on hostname or env var
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  // Dev environment: localhost or app.dev.codevet.dev
  if (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === 'app.dev.codevet.dev') {
    return 'https://api.dev.codevet.dev'
  }
  // Production: app.codevet.dev
  return 'https://api.codevet.dev'
}

const API_BASE_URL = getApiBaseUrl()

interface ApiError {
  error: string
  message: string
}

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    const response = await fetch(url, {
      ...options,
      headers,
      // Include cookies for authentication (session cookies set by backend OAuth flow)
      credentials: 'include',
    })

    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({
        error: 'unknown',
        message: `HTTP ${response.status}`,
      }))

      if (response.status === 401) {
        throw new Error('Unauthorized - please log in again')
      }

      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.')
      }

      throw new Error(errorData.message || `Request failed: ${response.status}`)
    }

    return response.json()
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const url = params
      ? `${endpoint}?${new URLSearchParams(params).toString()}`
      : endpoint
    return this.request<T>(url, { method: 'GET' })
  }

  async post<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async patch<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  }

  // Check if user is authenticated by calling the me endpoint
  async checkAuth(): Promise<boolean> {
    try {
      await this.get('/v1/me')
      return true
    } catch {
      return false
    }
  }

  // Logout by redirecting to the logout endpoint (clears session cookie)
  logout(): void {
    window.location.href = `${API_BASE_URL}/auth/logout`
  }
}

export const apiClient = new ApiClient()
